import * as React from "react";
import { cn } from "@/lib/utils";

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  error?: boolean;
  leadingIcon?: React.ReactNode;
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, error, leadingIcon, ...props }, ref) => (
    <div className={cn("relative flex w-full items-center", className)}>
      {leadingIcon && (
        <span aria-hidden className="pointer-events-none absolute left-3 text-muted">
          {leadingIcon}
        </span>
      )}
      <input
        ref={ref}
        className={cn(
          "h-[36px] w-full rounded-sm border px-3 text-sm text-ink transition-colors outline-none placeholder:text-subtle disabled:cursor-not-allowed disabled:bg-disabled-soft disabled:text-muted",
          leadingIcon && "pl-10",
          error
            ? "border-danger focus:shadow-[0_0_0_3px_var(--ads-danger-soft)]"
            : "border-line-strong focus:border-primary focus:shadow-[0_0_0_3px_var(--ads-primary-soft)]"
        )}
        {...props}
      />
    </div>
  )
);
Input.displayName = "Input";

export function Field({
  label,
  htmlFor,
  error,
  hint,
  required,
  children,
}: {
  label: string;
  htmlFor?: string;
  error?: string;
  hint?: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={htmlFor} className="text-xs font-semibold text-ink">
        {label}
        {required && <span className="ml-0.5 text-danger">*</span>}
      </label>
      {children}
      {hint && !error && <span className="text-xs text-muted">{hint}</span>}
      {error && <span className="text-xs text-danger">{error}</span>}
    </div>
  );
}

export { Input };